/**
 * ActionGroup — the chat tab's step timeline for one Asthra turn.
 *
 * Every tool/retrieval step a turn takes (live chat steps, live
 * edit-highlight steps, persisted ``chat_tool_step`` entries) renders
 * through this one component, modelled on Claude's "Used N tools" group:
 *
 *   - a quiet header with a chevron: "Working · N steps 0:21" while the
 *     turn runs (open, newest step pulsing), "Took N steps" once it has
 *     landed (folded to that one line until the writer opens it);
 *   - a thin vertical rail with one node per step — a quiet check when
 *     done, a pulsing brand dot while running, a muted red ring when the
 *     step failed or came back empty;
 *   - rows in writer language, with the result count / duration on the
 *     right, and an optional result card one press away.
 *
 * Pairs with StepRow, the renderer for one step on the rail.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import * as React from "react";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../tokens";

const pulse = {
  "0%, 100%": { opacity: 1 },
  "50%": { opacity: 0.35 },
};

const useStyles = makeStyles({
  group: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    margin: "2px 0",
    // Keeps the rail nodes' 1px border off the bubble edge (and clear of
    // the scroller's clip).
    paddingLeft: "2px",
    fontFamily: fonts.body,
  },
  header: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    width: "fit-content",
    // Left pad centres the live dot over the rail nodes below it.
    padding: "2px 0 2px 3.5px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    font: "inherit",
    fontSize: "12px",
    color: inkColors.ink3,
    textAlign: "left",
    ":hover": { color: inkColors.ink2 },
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
      borderRadius: "4px",
    },
  },
  timer: { fontVariantNumeric: "tabular-nums", color: inkColors.ink4 },
  chevron: {
    width: "10px",
    height: "10px",
    flexShrink: 0,
    color: inkColors.ink4,
    transitionProperty: "transform",
    transitionDuration: "150ms",
  },
  chevronClosed: { transform: "rotate(-90deg)" },
  liveDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: brandColors.primary,
    animationName: pulse,
    animationDuration: "1.1s",
    animationIterationCount: "infinite",
    "@media (prefers-reduced-motion: reduce)": { animationName: "none" },
  },
  rail: {
    position: "relative",
    listStyleType: "none",
    margin: "0 0 0 6px",
    padding: "0 0 0 16px",
    display: "flex",
    flexDirection: "column",
    gap: "1px",
    "::before": {
      content: '""',
      position: "absolute",
      left: "0",
      top: "9px",
      bottom: "9px",
      width: "1px",
      backgroundColor: surfaceColors.lineStrong,
    },
  },
  // ── StepRow ──
  step: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: "3px",
    padding: "3px 0",
    minWidth: 0,
  },
  node: {
    position: "absolute",
    left: "-21px",
    top: "6px",
    width: "11px",
    height: "11px",
    boxSizing: "border-box",
    borderRadius: "50%",
    border: `1px solid ${inkColors.ink5}`,
    backgroundColor: surfaceColors.paper,
    display: "grid",
    placeItems: "center",
    color: inkColors.ink4,
  },
  nodeRunning: {
    border: "none",
    backgroundColor: "transparent",
    "::after": {
      content: '""',
      width: "7px",
      height: "7px",
      borderRadius: "50%",
      backgroundColor: brandColors.primary,
      animationName: pulse,
      animationDuration: "1.1s",
      animationIterationCount: "infinite",
    },
    "@media (prefers-reduced-motion: reduce)": { "::after": { animationName: "none" } },
  },
  nodeError: {
    border: `1px solid ${statusColors.danger}`,
    "::after": {
      content: '""',
      width: "5px",
      height: "5px",
      borderRadius: "50%",
      backgroundColor: statusColors.danger,
      opacity: 0.75,
    },
  },
  check: { width: "7px", height: "7px" },
  row: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    width: "100%",
    padding: 0,
    background: "transparent",
    border: "none",
    font: "inherit",
    fontSize: "12px",
    lineHeight: 1.45,
    color: inkColors.ink2,
    textAlign: "left",
    minWidth: 0,
  },
  rowButton: {
    cursor: "pointer",
    "&:focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
      borderRadius: "4px",
    },
  },
  name: { minWidth: 0, overflowWrap: "anywhere" },
  meta: {
    marginLeft: "auto",
    paddingLeft: "8px",
    flexShrink: 0,
    fontSize: "10.5px",
    color: inkColors.ink4,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  rowChevron: {
    width: "9px",
    height: "9px",
    flexShrink: 0,
    alignSelf: "center",
    color: inkColors.ink4,
    transitionProperty: "transform",
    transitionDuration: "150ms",
  },
  rowChevronOpen: { transform: "rotate(90deg)" },
  summary: {
    fontSize: "11px",
    lineHeight: 1.4,
    color: inkColors.ink4,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflowWrap: "anywhere",
  },
  card: {
    border: `1px solid ${surfaceColors.line}`,
    backgroundColor: surfaceColors.paperDim,
    borderRadius: "8px",
    padding: "6px 10px",
    fontSize: "11.5px",
    lineHeight: 1.45,
    color: inkColors.ink3,
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
  },
});

const Chevron: React.FC<{ className: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 10 10" aria-hidden="true">
    <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);

export interface StepRowProps {
  name: string;
  /** Second line: what the step looked at (query, section, …). */
  summary?: string;
  /** Right-aligned: result count and/or duration ("1 result · 3.1s"). */
  meta?: string;
  status?: "running" | "done" | "error";
  /** What came back. When present, the row opens to show it in a card. */
  detail?: string;
}

/** One step on the rail: node, name, optional summary line and result card. */
export const StepRow: React.FC<StepRowProps> = ({ name, summary, meta, status = "done", detail }) => {
  const styles = useStyles();
  const [open, setOpen] = React.useState(false);
  const expandable = Boolean(detail);
  const rowContent = (
    <>
      <span className={styles.name}>{name}</span>
      {meta ? <span className={styles.meta}>{meta}</span> : null}
      {expandable ? (
        <svg
          className={mergeClasses(styles.rowChevron, open && styles.rowChevronOpen)}
          viewBox="0 0 10 10"
          aria-hidden="true"
        >
          <path d="M3.5 2 6.5 5 3.5 8" fill="none" stroke="currentColor" strokeWidth="1.3" />
        </svg>
      ) : null}
    </>
  );
  return (
    <li className={styles.step} data-step-status={status}>
      <span
        aria-hidden="true"
        className={mergeClasses(
          styles.node,
          status === "running" && styles.nodeRunning,
          status === "error" && styles.nodeError,
        )}
      >
        {status === "done" ? (
          <svg className={styles.check} viewBox="0 0 8 8" aria-hidden="true">
            <path d="M1.5 4.2 3.2 5.8 6.5 2.3" fill="none" stroke="currentColor" strokeWidth="1.2" />
          </svg>
        ) : null}
      </span>
      {expandable ? (
        <button
          type="button"
          className={mergeClasses(styles.row, styles.rowButton)}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {rowContent}
        </button>
      ) : (
        <div className={styles.row}>{rowContent}</div>
      )}
      {summary ? <span className={styles.summary}>{summary}</span> : null}
      {expandable && open ? <div className={styles.card}>{detail}</div> : null}
    </li>
  );
};

/** "0:21" — minutes:seconds since ``start``, ticking once a second. */
const LiveTimer: React.FC<{ start: number; className: string }> = ({ start, className }) => {
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const secs = Math.max(0, Math.floor((nowMs - start) / 1000));
  const text = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
  return (
    <span className={className} aria-hidden="true">
      {text}
    </span>
  );
};

export interface ActionGroupProps {
  count: number;
  /** True while the turn is still producing steps — open, pulsing header. */
  live?: boolean;
  /** Defaults to ``live``: a running turn shows its steps, a landed one folds. */
  defaultOpen?: boolean;
  /** Turn start (epoch ms) for the live header timer. */
  startedAt?: number | null;
  /** Accessible name of the step list. */
  listLabel?: string;
  children: React.ReactNode;
}

export const ActionGroup: React.FC<ActionGroupProps> = ({
  count,
  live = false,
  defaultOpen,
  startedAt = null,
  listLabel = "Steps",
  children,
}) => {
  const styles = useStyles();
  const [open, setOpen] = React.useState(defaultOpen ?? live);
  const steps = `${count} step${count === 1 ? "" : "s"}`;
  const label = live ? `Working · ${steps}` : `Took ${steps}`;
  return (
    <div className={styles.group} data-live={live || undefined}>
      <button
        type="button"
        className={styles.header}
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
      >
        {live ? <span className={styles.liveDot} aria-hidden /> : null}
        <span>{label}</span>
        {live && startedAt !== null ? <LiveTimer start={startedAt} className={styles.timer} /> : null}
        <Chevron className={mergeClasses(styles.chevron, !open && styles.chevronClosed)} />
      </button>
      {open ? (
        <ul className={styles.rail} aria-label={listLabel}>
          {children}
        </ul>
      ) : null}
    </div>
  );
};
